/**
 * EmailTemplatePreview — admin-only view for previewing React Email templates.
 *
 * Renders each template in an <iframe> so the admin can see exactly what
 * the email will look like. The iframe loads /api/services/email/preview?template=<name>
 * which returns rendered HTML.
 */

import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/features/states";
import { Mail, Eye, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

const TEMPLATES = [
  { key: "otp", label: "OTP / Verification", desc: "Registration, login, phone verify, password reset" },
  { key: "offer_made", label: "Offer Made", desc: "A courier made an offer on the customer's job" },
  { key: "offer_accepted", label: "Offer Accepted", desc: "The customer accepted the courier's offer" },
  { key: "job_assigned", label: "Job Assigned", desc: "The courier is now assigned to a job" },
  { key: "job_picked_up", label: "Job Picked Up", desc: "Parcel was picked up, on the way" },
  { key: "job_delivered", label: "Job Delivered", desc: "Parcel was delivered, awaiting confirmation" },
  { key: "payment_confirmed", label: "Payment Confirmed", desc: "Payment confirmed + commission breakdown" },
  { key: "trust_verified", label: "Trust Verified", desc: "ID verification was approved" },
  { key: "dispute_opened", label: "Dispute Opened", desc: "A dispute was opened on a job" },
  { key: "fraud_alert", label: "Fraud Alert", desc: "Admin-only fraud alert" },
  { key: "generic", label: "Generic", desc: "Fallback for ad-hoc notifications" },
] as const;

export function EmailTemplatePreview() {
  const { t } = useTranslation();
  const [active, setActive] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("admin.emailTemplates") || "Email Templates"}
        description={t("admin.emailTemplatesDesc") || "Preview transactional email templates (React Email)"}
        icon={Mail}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {TEMPLATES.map((tpl) => (
          <Card key={tpl.key} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <CardTitle className="text-sm">{tpl.label}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground min-h-[32px]">{tpl.desc}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  className="flex-1"
                  onClick={() => setActive(tpl.key)}
                >
                  <Eye className="mr-1.5 h-3.5 w-3.5" />
                  Preview
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(api.email.previewUrl(tpl.key), "_blank")}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-3xl h-[80vh] p-0 overflow-hidden">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-sm flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {TEMPLATES.find((t) => t.key === active)?.label}
            </DialogTitle>
          </DialogHeader>
          {active && (
            <iframe
              src={api.email.previewUrl(active)}
              className="flex-1 w-full border-0"
              style={{ height: "calc(80vh - 56px)" }}
              title="Email preview"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
